import { z } from 'zod';
import {
  PAGINATION_PAGE_SIZE_LARGE,
  PAGINATION_SORT_DIRECTION,
} from '~/constants';

import { toSnakeCase } from '@vestigas/utils';

import { ACCOUNTING_REFERENCE_DETAILS_SORT_BY } from './constants';

/**
 * Zod schema for validating accounting references details query parameters.
 */
export const zodAccountingReferencesDetailsQueryParamsSchema = z
  .object({
    dateRange: z.object({
      dateFrom: z.iso.date(),
      dateTo: z.iso.date(),
    }),
    limit: z
      .number()
      .int()
      .min(1)
      .max(500)
      .prefault(PAGINATION_PAGE_SIZE_LARGE),
    offset: z.number().int().nonnegative().prefault(0),
    page: z.number().int().nonnegative().optional(),
    searchString: z.string().trim().optional().prefault(''),
    sortBy: z
      .preprocess(
        (value) => (typeof value === 'string' ? toSnakeCase(value) : value),
        z.enum(ACCOUNTING_REFERENCE_DETAILS_SORT_BY),
      )
      .optional()
      .prefault('accref_name')
      .catch('accref_name'),
    sortOrder: z
      .preprocess(
        (value) => String(value).toUpperCase(),
        z.enum(PAGINATION_SORT_DIRECTION),
      )
      .optional()
      .prefault('DESC'),
  })
  .transform(({ limit, offset, page, ...rest }) => {
    return {
      ...rest,
      limit,
      offset: page ? page * limit : offset,
    };
  });

/**
 * Zod schema for validating a single accounting reference details table item.
 */
const zodAccountingReferenceDetailsItemSchema = z.object({
  accrefId: z.uuid(),
  accrefName: z.string(),
  assignedDeliveryNotesCount: z.number().int().nonnegative(),
  isActive: z.boolean(),
  signatureRate: z.number().nonnegative(),
  signedDeliveryNotesCount: z.number().int().nonnegative(),
  unsignedDeliveryNotesCount: z.number().int().nonnegative(),
});

/**
 * Zod schema for validating the accounting references details API response.
 */
export const zodAccountingReferencesDetailsResponseSchema = z.object({
  data: z.array(zodAccountingReferenceDetailsItemSchema),
  hasNextPage: z.boolean(),
  totalCount: z.number().int().nonnegative(),
});
